import { logger } from '@parrot-software/observability';

import type { IInvoiceDataApiPort } from '@subscriptions/application/ports';
import type { InvoiceDataViewDto } from '@subscriptions/application/dtos';
import { mapInvoiceDataToViewDto } from '@subscriptions/application/mappers';
import { extractErrorAttributes } from '@subscriptions/application/utils';

const invoiceLog = logger.forDomain('SUB', { subdomain: 'billing' });

/**
 * Use case for reading a single fiscal record.
 *
 * `includeDeleted` reaches one the listing hides: a retry link points at the record its payment
 * was invoiced with, and that record may have been deleted since.
 *
 * @param port - Invoice data API port
 * @returns Use case executor
 */
export const getInvoiceDataUseCase = (port: IInvoiceDataApiPort) => {
  const execute = async (invoiceDataUuid: string, includeDeleted = false): Promise<InvoiceDataViewDto> => {
    try {
      const invoiceData = await port.getById(invoiceDataUuid, includeDeleted);
      return mapInvoiceDataToViewDto(invoiceData);
    } catch (error) {
      // Warn, not error: a link pointing at a record that is gone is an expected outcome.
      invoiceLog.warn('getInvoiceData', {
        ...extractErrorAttributes(error),
        'invoice.dataUuid': invoiceDataUuid,
        'invoice.includeDeleted': includeDeleted,
      });
      throw error;
    }
  };

  return { execute };
};
